import { useCallback, useEffect, useRef, useState } from 'react';
import { noteIdAttribue, resoutReferences } from '../lib/identifiants';
import { surRafraichissement } from './rafraichissement';

/** Délai d'inactivité avant d'envoyer les modifications au serveur, en millisecondes. */
/** Délai entre deux tentatives de renvoi quand le réseau manque. */
const DELAI_RENVOI_MS = 15000;

const DELAI_ECRITURE_MS = 600;

// Une pièce qui désigne un enregistrement pas encore créé — le client tout
// juste repris des contacts — attend son identifiant : nouvel essai chaque
// seconde, deux minutes au plus.
const DELAI_ATTENTE_REFERENCE_MS = 1000;
const ESSAIS_ATTENTE_REFERENCE = 120;

// Écritures programmées ou en route, toutes collections confondues. Tant qu'il
// en reste, quitter la page les perdrait sans un mot : sur un serveur lent, la
// fenêtre dure plusieurs secondes, et une saisie faite juste avant de fermer
// l'onglet ne revenait pas au rechargement.
let ecrituresEnCours = 0;
let gardeInstallee = false;
// Collections dont la dernière salve a buté sur le réseau : leur saisie attend
// le retour de la connexion et n'existe, d'ici là, que dans l'onglet.
const renvoisEnAttente = new Set();

// Un lien « mailto: », « tel: » ou « sms: » ouvre une autre application : la
// page reste la. Le navigateur annonce pourtant un depart, et la confirmation
// surgissait sur un lien de courriel touche juste apres une saisie. On retient
// l'instant du dernier appui sur un tel lien pour ne pas s'y tromper.
const LIEN_QUI_NE_QUITTE_PAS = /^(mailto|tel|sms|whatsapp):/i;
const DELAI_LIEN_MS = 1500;
let dernierLienExterne = 0;

function noteLeLien(evenement) {
  const lien = evenement.target?.closest?.('a[href]');
  if (lien && LIEN_QUI_NE_QUITTE_PAS.test(lien.getAttribute('href') || '')) {
    dernierLienExterne = Date.now();
  }
}

function debutEcriture() {
  ecrituresEnCours += 1;
  if (!gardeInstallee && typeof window !== 'undefined') {
    window.addEventListener('click', noteLeLien, true);
    window.addEventListener('beforeunload', (e) => {
      if (Date.now() - dernierLienExterne < DELAI_LIEN_MS) return;
      if (ecrituresEnAttente()) {
        e.preventDefault();
        // Les navigateurs plus anciens n'affichent la confirmation qu'avec cette valeur.
        e.returnValue = '';
      }
    });
    gardeInstallee = true;
  }
}

function finEcriture() {
  ecrituresEnCours = Math.max(0, ecrituresEnCours - 1);
}

/**
 * @returns {boolean} vrai tant qu'une modification n'est pas arrivée au serveur
 */
export function ecrituresEnAttente() {
  return ecrituresEnCours > 0 || renvoisEnAttente.size > 0;
}

/**
 * Collection persistée en base, synchronisée dans les deux sens.
 *
 * <p>L'interface manipule ses collections avec des `setState` classiques, hérités
 * du prototype. Plutôt que de réécrire chaque point de mutation des écrans, ce
 * hook observe la collection : à chaque modification locale il la compare à
 * l'état du serveur et en déduit les créations, les mises à jour et les
 * suppressions à envoyer.
 *
 * <p>Un élément est considéré comme nouveau tant que son identifiant n'est pas un
 * nombre : l'interface attribue des identifiants temporaires (`uid()`, `"04"`),
 * la base attribue des entiers. Après chaque envoi, la collection est rechargée
 * pour que les identifiants attribués par la base remplacent les temporaires.
 *
 * @param {object} options configuration de la synchronisation
 * @param {() => Promise<any>} options.charge chargement distant
 * @param {(dto: any) => any} options.versInterface conversion API → interface
 * @param {(item: any) => any} options.versApi conversion interface → API
 * @param {(dto: any) => Promise<any>} options.cree création distante
 * @param {(id: number, dto: any) => Promise<any>} options.metAJour mise à jour distante
 * @param {(id: number) => Promise<any>} [options.supprime] suppression distante
 * @param {boolean} [options.actif] false pour différer le chargement
 * @returns {{donnees: any[], setDonnees: Function, chargement: boolean,
 *            erreur: Error|null, enregistrement: boolean, recharge: Function}}
 *          l'état de la collection et ses commandes
 */
/**
 * La pièce que le serveur a déjà enregistrée sous le numéro proposé, quand
 * c'est bien la même : nouvelle depuis le dernier chargement, même client,
 * même date, autant de lignes. Sinon rien : le numéro a été pris par une
 * autre pièce, et la nôtre part sous le numéro suivant.
 *
 * @param {Function} charge relit la collection
 * @param {object} dto pièce envoyée
 * @param {Map<number, object>} connus pièces connues avant l'envoi
 * @returns {Promise<object|null>} la pièce déjà créée, ou null
 */
export async function pieceDejaCreee(charge, dto, connus) {
  let reponse;
  try {
    reponse = await charge();
  } catch {
    return null;
  }
  const dtos = Array.isArray(reponse) ? reponse : (reponse?.content ?? []);
  const meme = (a, b) => (a ?? null) === (b ?? null);
  return (
    dtos.find(
      (d) =>
        typeof d?.id === 'number' &&
        !connus?.has(d.id) &&
        d.numero === dto.numero &&
        meme(d.clientId, dto.clientId) &&
        (!dto.dateDocument || !d.dateDocument || d.dateDocument === dto.dateDocument) &&
        (!Array.isArray(dto.lignes) ||
          !Array.isArray(d.lignes) ||
          d.lignes.length === dto.lignes.length),
    ) ?? null
  );
}

export function useSyncedCollection({
  charge,
  versInterface,
  versApi,
  cree,
  metAJour,
  supprime,
  actif = true,
}) {
  const [donnees, setDonnees] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState(null);

  // Dernier état connu du serveur, indexé par identifiant : sert de référence
  // pour ne réécrire que ce qui a réellement changé.
  const serveur = useRef(new Map());
  // Vrai tant que le premier chargement n'a pas eu lieu : sans cette garde, la
  // collection vide initiale serait interprétée comme une suppression générale.
  const premierChargementFait = useRef(false);
  // Vrai pendant qu'on réinjecte l'état du serveur : évite qu'une réconciliation
  // ne soit prise pour une modification de l'utilisateur.
  const reconciliation = useRef(false);
  const monte = useRef(true);
  // Compteur des modifications locales : une saisie faite pendant qu'une
  // salve est en route ne doit pas etre ecrasee par le rechargement qui la
  // suit — c'est ce qui arrivait, sans un mot, sur une connexion lente.
  const version = useRef(0);
  const dernieres = useRef([]);
  // Une salve refusee faute de reseau attend le retour du reseau.
  const aRenvoyer = useRef(false);
  // Pièces retenues parce qu'elles désignent un enregistrement que le serveur
  // n'a pas encore créé, et nombre d'essais déjà faits.
  const enAttenteDeReference = useRef(false);
  const essaisAttente = useRef(0);
  // Vrai pendant une salve : le nouvel essai d'une pièce retenue l'attend.
  const salveEnCours = useRef(false);
  // Une salve demandée pendant qu'une autre est en route attend sa fin : deux
  // salves en même temps créaient deux fois la même dépense sur un serveur
  // lent (un café enregistré, une essence saisie pendant la réponse).
  const aRelancer = useRef(false);
  // Identifiants donnés par la base aux éléments créés, par identifiant
  // provisoire : une liste encore vieille d'un instant ne recrée rien.
  const creesParProvisoire = useRef(new Map());

  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
      // L'écran fermé n'enverra plus rien : il ne retient plus la page.
      renvoisEnAttente.delete(aRenvoyer);
      renvoisEnAttente.delete(enAttenteDeReference);
    };
  }, []);

  const attendRenvoi = (oui) => {
    aRenvoyer.current = oui;
    if (oui) renvoisEnAttente.add(aRenvoyer);
    else renvoisEnAttente.delete(aRenvoyer);
  };

  const recharge = useCallback(async () => {
    try {
      const reponse = await charge();
      const dtos = Array.isArray(reponse) ? reponse : (reponse?.content ?? []);

      if (!monte.current) return;

      serveur.current = new Map(dtos.map((dto) => [dto.id, dto]));
      reconciliation.current = true;
      setDonnees(dtos.map(versInterface));
      setErreur(null);
      premierChargementFait.current = true;
    } catch (e) {
      if (monte.current) setErreur(e);
    } finally {
      if (monte.current) setChargement(false);
    }
  }, [charge, versInterface]);

  useEffect(() => {
    if (!actif) {
      setChargement(false);
      return;
    }
    recharge();
  }, [actif, recharge]);

  // Ce qu'un ouvrier saisit sur son téléphone — pointage, congé, photo,
  // rapport — n'arrivait au bureau qu'au rechargement complet de la page. La
  // collection se relit au rythme du module de rafraîchissement, et ne se
  // réécrit que si le serveur a du nouveau : rien ne bouge à l'écran sinon.
  // Une saisie en cours a toujours le dernier mot — rien n'est relu tant
  // qu'une écriture attend, et une relecture arrivée après une frappe est
  // jetée.
  useEffect(() => {
    if (!actif) return undefined;
    return surRafraichissement(async () => {
      if (!premierChargementFait.current || ecrituresEnAttente()) return;
      const versionDepart = version.current;
      let dtos;
      try {
        const reponse = await charge();
        dtos = Array.isArray(reponse) ? reponse : (reponse?.content ?? []);
      } catch (e) {
        // Serveur injoignable : l'écran garde ce qu'il a, sans un mot. La
        // prochaine écriture ou le prochain tour le dira s'il le faut.
        return;
      }
      if (!monte.current || version.current !== versionDepart || ecrituresEnAttente()) return;
      if (memesDonnees(dtos, serveur.current)) return;
      serveur.current = new Map(dtos.map((dto) => [dto.id, dto]));
      reconciliation.current = true;
      setDonnees(dtos.map(versInterface));
    });
  }, [actif, charge, versInterface]);

  // Écriture différée : les écrans enchaînent souvent plusieurs `setState` pour
  // une seule action de l'utilisateur, qu'il serait inutile d'envoyer une à une.
  useEffect(() => {
    if (!actif || !premierChargementFait.current) return;

    if (reconciliation.current) {
      reconciliation.current = false;
      return;
    }
    version.current += 1;
    dernieres.current = donnees;

    debutEcriture();
    let parti = false;
    const minuteur = setTimeout(() => {
      parti = true;
      envoie(donnees).finally(finEcriture);
    }, DELAI_ECRITURE_MS);

    return () => {
      clearTimeout(minuteur);
      // Remplacée par une saisie plus récente, qui compte à son tour.
      if (!parti) finEcriture();
    };
    // `envoie` est stable pour une configuration donnée ; ne dépendre que des
    // données évite de relancer une écriture à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donnees, actif]);

  // Le reseau revient : ce qui attendait repart, sans que l'utilisateur ait
  // a retoucher quoi que ce soit.
  useEffect(() => {
    if (!actif) return undefined;
    const relance = () => {
      if (aRenvoyer.current) envoie(dernieres.current);
    };
    window.addEventListener('online', relance);
    const minuteur = setInterval(relance, DELAI_RENVOI_MS);
    return () => {
      window.removeEventListener('online', relance);
      clearInterval(minuteur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif]);

  /** Compare la collection locale à l'état du serveur et envoie les écarts. */
  async function envoie(brutes) {
    if (salveEnCours.current) {
      aRelancer.current = true;
      return;
    }
    const locales = brutes.map((item) =>
      typeof item.id !== 'number' && creesParProvisoire.current.has(item.id)
        ? { ...item, id: creesParProvisoire.current.get(item.id) }
        : item,
    );
    const idsLocaux = new Set(
      locales.map((item) => item.id).filter((id) => typeof id === 'number'),
    );

    const aSupprimer = supprime
      ? [...serveur.current.keys()].filter((id) => !idsLocaux.has(id))
      : [];
    const aCreer = locales.filter((item) => typeof item.id !== 'number');
    const aMettreAJour = locales.filter((item) => typeof item.id === 'number' && aChange(item));

    if (aSupprimer.length === 0 && aCreer.length === 0 && aMettreAJour.length === 0) {
      return;
    }

    setEnregistrement(true);
    salveEnCours.current = true;
    const versionDepart = version.current;
    // Un element cree recoit son identifiant de la base ; on le retient pour le
    // rendre a l'element local s'il a ete retouche pendant l'envoi.
    const idsCrees = new Map();
    let premiereErreur = null;
    const tente = async (operation) => {
      try {
        await operation();
      } catch (e) {
        // Une ecriture refusee — une facture payee qu'on ne reecrit pas —
        // n'empeche pas les autres : la salve continue, et l'erreur est
        // montree a la fin. S'arreter a la premiere perdait les creations.
        if (!premiereErreur) premiereErreur = e;
        if (e?.reseau) throw e;
      }
    };
    try {
      // Les suppressions d'abord : elles libèrent les contraintes d'unicité que
      // les créations suivantes pourraient rencontrer.
      for (const id of aSupprimer) {
        await tente(() => supprime(id));
      }
      // Une offre qui désigne un client tout juste créé part avec le numéro
      // que le serveur lui a donné ; s'il n'en a pas encore, elle attend.
      // Envoyée avec l'identifiant provisoire, elle était refusée et perdue.
      let retenue = false;
      const prepare = (item) => {
        const { dto, enAttente } = resoutReferences(versApi(item));
        if (enAttente) retenue = true;
        return enAttente ? null : dto;
      };
      for (const item of aMettreAJour) {
        const dto = prepare(item);
        if (dto) await tente(() => metAJour(item.id, dto));
      }
      for (const item of aCreer) {
        const dto = prepare(item);
        if (!dto) continue;
        await tente(async () => {
          let cree_;
          try {
            cree_ = await cree(dto);
          } catch (e) {
            // Le numéro proposé par l'écran a été pris entre-temps par un
            // collègue : la pièce part sous le numéro libre suivant, que le
            // serveur donne. Refusée, elle était perdue avec toute la saisie.
            if (!(e?.status === 409 && !e.estLimiteFormule && dto?.numero)) throw e;
            // Le refus peut répondre à un premier envoi qui, lui, a abouti
            // (réseau coupé avant la réponse, puis renvoi) : la pièce existe
            // déjà sous ce numéro. On la reprend, plutôt que d'en créer une
            // seconde sous le numéro suivant.
            const deja = await pieceDejaCreee(charge, dto, serveur.current);
            cree_ = deja ?? (await cree({ ...dto, numero: null }));
          }
          if (cree_ && typeof cree_.id === 'number') {
            idsCrees.set(item.id, cree_.id);
            creesParProvisoire.current.set(item.id, cree_.id);
            noteIdAttribue(item.id, cree_.id);
          }
        });
      }
      attendRenvoi(false);
      retient(retenue);
      if ((version.current === versionDepart || premiereErreur) && !retenue) {
        // Rien n'a bouge pendant l'envoi : la base fait foi. Apres un refus
        // aussi : l'ecran ne doit pas garder une valeur que la base n'a pas
        // acceptee.
        await recharge();
      } else {
        // L'utilisateur a continue de saisir : on garde sa saisie, en donnant
        // aux elements crees l'identifiant que la base leur a attribue. La
        // prochaine salve n'enverra que ce qui differe encore.
        const reponse = await charge();
        const dtos = Array.isArray(reponse) ? reponse : (reponse?.content ?? []);
        if (!monte.current) return;
        serveur.current = new Map(dtos.map((dto) => [dto.id, dto]));
        // Rien de cree (une piece seulement retenue) : rien a reecrire, et
        // surtout pas de salve de plus en meme temps que le prochain essai.
        if (idsCrees.size > 0) {
          setDonnees((courantes) =>
            courantes.map((item) =>
              idsCrees.has(item.id) ? { ...item, id: idsCrees.get(item.id) } : item,
            ),
          );
        }
      }
      if (premiereErreur && monte.current) setErreur(premiereErreur);
    } catch (e) {
      if (e?.reseau) {
        // Pas de reseau : la saisie reste a l'ecran et repartira d'elle-meme.
        attendRenvoi(true);
        if (monte.current) setErreur(e);
      } else {
        await recharge();
        if (monte.current) setErreur(e);
      }
    } finally {
      salveEnCours.current = false;
      if (monte.current) setEnregistrement(false);
      if (aRelancer.current && monte.current) {
        aRelancer.current = false;
        debutEcriture();
        envoie(dernieres.current).finally(finEcriture);
      }
    }
  }

  /**
   * Retient ou libère les pièces qui attendent l'identifiant d'un
   * enregistrement, et programme le prochain essai. La page ne se quitte pas
   * sans avertissement tant qu'une pièce attend.
   */
  function retient(oui) {
    enAttenteDeReference.current = oui;
    if (!oui) {
      essaisAttente.current = 0;
      renvoisEnAttente.delete(enAttenteDeReference);
      return;
    }
    renvoisEnAttente.add(enAttenteDeReference);
    essaisAttente.current += 1;
    if (essaisAttente.current > ESSAIS_ATTENTE_REFERENCE) {
      renvoisEnAttente.delete(enAttenteDeReference);
      if (monte.current) {
        setErreur(
          new Error(
            'Le client de cette pièce n’a pas pu être enregistré : vérifie la fiche client, puis enregistre à nouveau.',
          ),
        );
      }
      return;
    }
    const essaie = () => {
      if (!monte.current || !enAttenteDeReference.current) return;
      if (salveEnCours.current) setTimeout(essaie, DELAI_ATTENTE_REFERENCE_MS);
      else envoie(dernieres.current);
    };
    setTimeout(essaie, DELAI_ATTENTE_REFERENCE_MS);
  }

  /** Vrai si l'élément diffère de ce que le serveur connaît. */
  function aChange(item) {
    const connu = serveur.current.get(item.id);
    if (!connu) return true;
    return !equivalents(versApi(item), connu);
  }

  return { donnees, setDonnees, chargement, erreur, enregistrement, recharge };
}

/**
 * Vrai si ce que le serveur rend est, mot pour mot, ce qu'on en connaît déjà.
 *
 * <p>Comparer avant d'écrire évite un nouveau rendu de tous les écrans à
 * chaque minute, et le clignotement qui irait avec.
 *
 * @param {object[]} dtos représentation fraîchement reçue
 * @param {Map<any, object>} connus dernier état connu, indexé par identifiant
 * @returns {boolean} vrai si rien n'a changé
 */
function memesDonnees(dtos, connus) {
  if (dtos.length !== connus.size) return false;
  return dtos.every((dto) => {
    const connu = connus.get(dto.id);
    return connu !== undefined && JSON.stringify(dto) === JSON.stringify(connu);
  });
}

/**
 * Compare la charge utile à envoyer avec celle reçue du serveur.
 *
 * <p>Seuls les champs que l'interface renseigne sont comparés : le serveur
 * renvoie des champs calculés (totaux, libellés joints, horodatages) qui ne
 * figurent pas dans la charge utile et ne doivent pas déclencher d'écriture.
 *
 * @param {object} envoye charge utile qui serait envoyée
 * @param {object} recu représentation renvoyée par le serveur
 * @param {boolean} [dansUneListe] élément d'une liste (les lignes d'une
 *   facture) : son rang est celui de la liste, et sa `position` ne compte pas
 * @returns {boolean} vrai si les deux représentations concordent
 */
export function equivalents(envoye, recu, dansUneListe = false) {
  for (const [cle, valeur] of Object.entries(envoye)) {
    if (valeur === undefined) continue;
    // Les lignes que le serveur crée lui-même (facture d'acompte, facture
    // finale) se numérotent à partir de 1, l'écran à partir de 0 : la même
    // ligne passait pour modifiée, et une facture payée réécrite pour rien
    // affichait « Une facture payée ne peut plus être modifiée ». L'ordre de
    // la liste, lui, est toujours comparé.
    if (dansUneListe && cle === 'position') continue;
    // « connus » (ce que l'écran a lu du serveur, pour ne pas effacer la ligne
    // d'un collègue) part avec le chantier mais ne revient jamais : il faisait
    // passer tous les chantiers pour modifiés, et la moindre saisie les
    // renvoyait tous au serveur.
    if (cle === 'connus' && !(cle in (recu || {}))) continue;
    if (!estEquivalent(valeur, recu?.[cle])) return false;
  }
  return true;
}

/** Vrai pour l'absence de valeur comme pour la chaîne vide. */
function estVide(valeur) {
  return valeur == null || valeur === '';
}

function estEquivalent(a, b, dansUneListe = false) {
  if (a === b) return true;
  // Un champ texte laissé vide et un champ absent disent la même chose : les
  // écrans rendent `''` ce que le serveur renvoie `null`, et les distinguer
  // faisait réécrire à chaque action tout enregistrement portant une note, un
  // contact ou une mention vide — c'est-à-dire presque tous.
  if (estVide(a) && estVide(b)) return true;
  if (a == null || b == null) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => estEquivalent(v, b[i], true));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    return equivalents(a, b, dansUneListe);
  }
  // Les montants reviennent parfois en nombre, parfois en chaîne selon le
  // sérialiseur : la comparaison numérique évite de fausses différences.
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;

  return String(a) === String(b);
}

/**
 * Ressource singleton persistée en base, comme les paramètres de la société.
 *
 * @param {object} options configuration de la synchronisation
 * @param {() => Promise<any>} options.charge chargement distant
 * @param {(dto: any) => any} options.versInterface conversion API → interface
 * @param {(item: any) => any} options.versApi conversion interface → API
 * @param {(dto: any) => Promise<any>} options.enregistre écriture distante
 * @param {boolean} [options.actif] false pour différer le chargement
 * @returns {{valeur: any, setValeur: Function, chargement: boolean, erreur: Error|null}}
 *          l'état de la ressource et ses commandes
 */
export function useSyncedSingleton({ charge, versInterface, versApi, enregistre, actif = true }) {
  const [valeur, setValeur] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const serveur = useRef(null);
  const premierChargementFait = useRef(false);
  const reconciliation = useRef(false);
  const monte = useRef(true);
  // Numéro de la dernière saisie : la réponse d'un enregistrement ne remplace
  // la valeur locale que si rien n'a été saisi depuis son départ.
  const saisie = useRef(0);

  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
    };
  }, []);

  useEffect(() => {
    if (!actif) {
      setChargement(false);
      return undefined;
    }

    let annule = false;
    setChargement(true);
    charge()
      .then((dto) => {
        if (annule || !monte.current) return;
        serveur.current = dto;
        reconciliation.current = true;
        setValeur(dto ? versInterface(dto) : null);
        premierChargementFait.current = true;
      })
      .catch((e) => {
        if (!annule && monte.current) setErreur(e);
      })
      .finally(() => {
        if (!annule && monte.current) setChargement(false);
      });

    return () => {
      annule = true;
    };
  }, [actif, charge, versInterface]);

  useEffect(() => {
    if (!actif || !premierChargementFait.current || valeur == null) return;

    if (reconciliation.current) {
      reconciliation.current = false;
      return;
    }

    const charge_utile = versApi(valeur);
    if (serveur.current && equivalents(charge_utile, serveur.current)) return;

    saisie.current += 1;
    const envoyee = saisie.current;
    debutEcriture();
    let parti = false;
    const minuteur = setTimeout(() => {
      parti = true;
      enregistre(charge_utile)
        .then((dto) => {
          if (!monte.current) return;
          serveur.current = dto;
          setErreur(null);
          // Une saisie faite pendant l'aller-retour (un taux de TVA tapé
          // juste après un autre réglage, un profil plus lourd à envoyer) était
          // remplacée par la copie du serveur, qui ne la portait pas, et comme
          // cette copie passait pour réconciliée, elle n'était jamais renvoyée.
          // On garde la valeur locale : son propre enregistrement est en route.
          if (saisie.current !== envoyee) return;
          reconciliation.current = true;
          setValeur(versInterface(dto));
        })
        .catch((e) => {
          if (monte.current) setErreur(e);
        })
        .finally(finEcriture);
    }, DELAI_ECRITURE_MS);

    return () => {
      clearTimeout(minuteur);
      if (!parti) finEcriture();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valeur]);

  return { valeur, setValeur, chargement, erreur };
}
